/// <amd-module name="VSS/Diagnostics/UI/Components/Insights/Insights" />
import * as React from "react";

import { caseInsensitiveContains, endsWith, equals } from "VSS/Core/Util/String";
import { Segment } from "VSS/Diagnostics/UI/Segment";
import { Level } from "VSS/Platform/Contracts/ClientTrace";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IPerformanceInsight, IVssPerformanceService } from "VSS/Platform/Performance";
import { groupBy } from "VSS/Platform/Util/Array";
import { Tooltip } from "VSSUI/TooltipEx";

interface IPerfInsightsSectionProps {}

interface IPerfInsightsSectionState {
    legacyAjaxCallCount: number;
    warnings: number;
    errors: number;

    couldBeFPS: boolean;
    fpsReferrer?: string;
    pageInsights?: { [groupName: string]: IPerformanceInsight[] };
    displayedInsightsGroup?: string;
}

class Insights extends VssComponent<IPerfInsightsSectionProps, IPerfInsightsSectionState> {
    private _originalWarn: ((message?: any, ...optionalParams: any[]) => void) | undefined;
    private _originalError: ((message?: any, ...optionalParams: any[]) => void) | undefined;

    constructor(props: IPerfInsightsSectionProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            legacyAjaxCallCount: 0,
            warnings: 0,
            errors: 0,
            couldBeFPS: false,
            pageInsights: this._collectPageInsights()
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        // check for potential FPS switch
        try {
            // if length of history is 1 we're in a new tab/window and there is no possibility for an FPS
            if (window.history.length > 1) {
                // for now we only want to check requests within a project context
                const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
                const project = navigationService.getCurrentRoute().routeValues.project || "";
                const referrer = document.referrer;
                if (project && (endsWith(referrer, `/${project}`, true) || caseInsensitiveContains(referrer, `/${project}/`))) {
                    // we are in a project context, and the referrer contains the current project as a path segment
                    // so assume that the previous request came from the same project.

                    // check whether the page was reloaded
                    const performanceApiSupported: boolean = performance && !!performance.navigation;
                    if (
                        (performanceApiSupported && performance.navigation.type !== performance.navigation.TYPE_RELOAD) ||
                        (!performanceApiSupported && !equals(referrer, window.location.href, true))
                    ) {
                        // request was not a refresh, check if it's the same host. If it is, it means that was a
                        // candidate for an FPS
                        if (caseInsensitiveContains(referrer, window.location.host)) {
                            this.setState({
                                couldBeFPS: true,
                                fpsReferrer: referrer.toString()
                            });
                        }
                    }
                }
            }
        } catch {
            // ignore error
        }

        this.addEventListener(document, "performance.legacyAjaxCall", this._callMade);

        if (console) {
            this._originalWarn = this.replaceConsoleMethod("warn", "warnings");
            this._originalError = this.replaceConsoleMethod("error", "errors");
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        if (this._originalWarn) {
            console.warn = this._originalWarn;
            console.error = this._originalError!;
        }
    }

    public render(): JSX.Element {
        const { legacyAjaxCallCount, couldBeFPS, fpsReferrer, pageInsights, displayedInsightsGroup, errors, warnings } = this.state;
        const alpha: number = legacyAjaxCallCount > 0 ? 0.1 + legacyAjaxCallCount / 60.0 : 0;

        let insightsForDialog: IPerformanceInsight[] | undefined = undefined;

        if (pageInsights && displayedInsightsGroup) {
            insightsForDialog = pageInsights[displayedInsightsGroup];
        }

        const allGood: boolean =
            legacyAjaxCallCount === 0 && !couldBeFPS && errors === 0 && warnings === 0 && (!pageInsights || Object.keys(pageInsights).length === 0);

        return (
            <span className="perf-section flex-row flex-center">
                Insights
                {allGood && (
                    <Tooltip text="All good">
                        <span>✅</span>
                    </Tooltip>
                )}
                {legacyAjaxCallCount > 0 && (
                    <span
                        style={{ backgroundColor: `rgba(192, 0, 0, ${alpha})` }}
                        title="MVC API calls are deprecated and should be replaced using data providers and REST API calls."
                    >
                        <Segment dataSegment={{ segmentLabel: "MVC API", segmentContent: legacyAjaxCallCount.toString(10) }} />
                    </span>
                )}
                {errors > 0 && (
                    <Segment
                        dataSegment={{ segmentLabel: "Errors", segmentContent: errors.toString(10) }}
                        tooltipProps={{ className: "perf-insight-error perf-insight-no-action", text: "There are console errors on this page." }}
                    />
                )}
                {warnings > 0 && (
                    <Segment
                        dataSegment={{ segmentLabel: "Warnings", segmentContent: warnings.toString(10) }}
                        tooltipProps={{ className: "perf-insight-error perf-insight-no-action", text: "There are console warnings on this page." }}
                    />
                )}
                {couldBeFPS && (
                    <Segment
                        dataSegment={{ segmentLabel: "Navigation", segmentContent: "NO FPS" }}
                        tooltipProps={{
                            className: "perf-insight-error perf-insight-no-action",
                            text: `The last navigation was NOT a FPS. This might be a problem with the current page or the last one: '${fpsReferrer}'`
                        }}
                    />
                )}
                {pageInsights &&
                    Object.keys(pageInsights).map(group => {
                        const insights = pageInsights[group];
                        const level = insights[0].level;
                        const className = level === Level.Error ? "perf-insight-error" : level === Level.Warning ? "perf-insight-warning" : undefined;
                        return (
                            <Segment
                                key={"insight-group-" + group}
                                className={className}
                                dataSegment={{ segmentLabel: group, segmentContent: insights.length.toString() }}
                                onClick={() => {
                                    this.setState({ displayedInsightsGroup: group });
                                }}
                            />
                        );
                    })}
                {insightsForDialog && (
                    <WrappedComponent
                        dependencies={["ms.vss-features.diag-dialog-content"]}
                        wrappedType="vss.perf-insights-dialog"
                        onDismiss={this.onDismiss}
                        insightsForDialog={insightsForDialog}
                        displayedInsightsGroup={displayedInsightsGroup}
                    />
                )}
            </span>
        );
    }

    private replaceConsoleMethod(
        methodName: "warn" | "error",
        stateName: "warnings" | "errors"
    ): ((message?: any, ...optionalParams: any[]) => void) | undefined {
        const originalMethod = console[methodName];

        const _this = this;
        console[methodName] = function() {
            // Schedule the update on another tick since the warning/error might have been fired in another component's render cycle, for example
            _this.setTimeout(() => {
                _this.setState(s => {
                    const newState = {} as IPerfInsightsSectionState;
                    newState[stateName] = s[stateName] + 1;
                    return newState;
                });
            }, 0);

            // Call original method
            if (originalMethod) {
                // @ts-ignore don't use `arguments` - prefer ...rest parameters.
                originalMethod.apply(this, arguments);
            }
        };

        return originalMethod;
    }

    private _callMade = () => {
        this.setState({
            legacyAjaxCallCount: this.state.legacyAjaxCallCount + 1
        });
    };

    private _collectPageInsights(): { [groupName: string]: IPerformanceInsight[] } | undefined {
        const insights = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").getPerformanceInsights();
        if (insights) {
            const groups = groupBy(insights, insight => insight.groupName);
            for (const groupKey in groups) {
                // sort by level ascending (lower level is more important)
                groups[groupKey].sort((i1, i2) => (i1.level || Level.Info) - (i2.level || Level.Info));
            }
            return groups;
        }

        return undefined;
    }

    private onDismiss = () => {
        this.setState({
            displayedInsightsGroup: undefined
        });
    };
}

Components.add("perfbar.insightsSection", Insights);
